// ─── Verrou côté navigateur contre la résurrection d'une fiche supprimée ──
//
// Créé le 2026-08-08, après le retour de trois fiches supprimées la veille en
// production.
//
// CE QU'ON A TROUVÉ. `pullFromCloud` considère comme « locale seulement »
// toute fiche présente en localStorage et absente de la base, et la repousse —
// puis pousse ses générations. Une fiche supprimée DÉFINITIVEMENT (DELETE, pas
// corbeille) disparaît de la base sans laisser de trace lisible par le
// navigateur : elle rentre donc exactement dans ce cas, et revient.
//
// POURQUOI LE REGISTRE LOCAL DES SUPPRESSIONS NE SUFFISAIT PAS.
// `bfc_deleted_ids` n'apprend une suppression que de deux façons : l'appareil
// l'a faite lui-même, ou il a vu passer un tombstone `is_deleted=true` encore
// présent en base. Une suppression définitive faite ailleurs — éditeur SQL,
// script d'administration, purge — ne coche ni l'une ni l'autre.
//
// LE DISCRIMINANT N'EST PAS L'ABSENCE, C'EST L'HISTOIRE.
//
//   « absente de la base » n'est PAS une preuve de suppression : une fiche
//   neuve créée hors ligne est absente elle aussi, et sa création est
//   parfaitement légitime. Refuser toute poussée d'une fiche absente
//   interdirait de créer une cliente.
//
//   Ce qui distingue les deux cas, c'est de savoir si cet identifiant a DÉJÀ
//   ÉTÉ VU EN LIGNE. Jamais vu → création. Vu, puis disparu → rejeu.
//
// Ce module ne supprime jamais rien. Il refuse d'ÉCRIRE, c'est tout : la
// donnée locale reste intacte, consultable et exportable.

const VUS_EN_LIGNE_KEY = 'bfc_ids_vus_en_ligne';

/** Garde-fou de volume : le cockpit gère des dizaines de fiches, pas des milliers. */
const MAX_VUS = 5000;

export const MOTIF_POUSSEE = Object.freeze({
  CREATION: 'creation',
  TOMBSTONE: 'tombstone',
  REJEU: 'rejeu',
  LECTURE_SUSPECTE: 'lecture_suspecte',
});

/**
 * Cette fiche peut-elle être poussée vers la base alors qu'elle n'y est pas ?
 *
 * Fonction PURE : toute la décision tient dans ses trois entrées, ce qui la
 * rend testable sans navigateur ni réseau.
 *
 * @param {object} etat
 * @param {boolean} etat.tombstone       - identifiant présent au registre serveur des suppressions définitives
 * @param {boolean} etat.dejaVuEnLigne   - identifiant déjà observé dans une lecture de la base
 * @param {boolean} etat.lectureSuspecte - la lecture de la base est-elle exploitable ? (voir `lectureEstSuspecte`)
 * @returns {{ autorisee: boolean, motif: string, explication: string }}
 */
export function deciderPousseeClient(etat = {}) {
  const { tombstone = false, dejaVuEnLigne = false, lectureSuspecte = false } = etat;

  // 1. Suppression définitive attestée par le serveur. Aucun doute possible.
  if (tombstone) {
    return {
      autorisee: false,
      motif: MOTIF_POUSSEE.TOMBSTONE,
      explication: 'identifiant definitivement supprime (registre serveur) — recreation refusee',
    };
  }

  // 2. Lecture inexploitable : on ne conclut RIEN. Ni poussée, ni suppression
  //    locale. Une session sans droits lit zéro fiche ; en déduire que tout a
  //    été supprimé, ou tout repousser, seraient deux erreurs symétriques.
  if (lectureSuspecte) {
    return {
      autorisee: false,
      motif: MOTIF_POUSSEE.LECTURE_SUSPECTE,
      explication: 'lecture de la base non concluante — aucune ecriture tant que l etat distant est incertain',
    };
  }

  // 3. Déjà vue en ligne, puis disparue : c'est un rejeu, pas une création.
  if (dejaVuEnLigne) {
    return {
      autorisee: false,
      motif: MOTIF_POUSSEE.REJEU,
      explication: 'fiche deja vue en ligne puis disparue — rejeu refuse, la donnee locale est conservee',
    };
  }

  // 4. Identifiant jamais vu en ligne : création légitime.
  return {
    autorisee: true,
    motif: MOTIF_POUSSEE.CREATION,
    explication: 'identifiant jamais vu en ligne — creation legitime',
  };
}

/**
 * La lecture de la base est-elle exploitable pour conclure « cette fiche a
 * été supprimée » ?
 *
 * Elle ne l'est pas quand la base rend ZÉRO fiche alors que le local en porte.
 * Ce cas arrive sans qu'aucune suppression n'ait eu lieu : session expirée,
 * RLS qui masque tout (V97.178 réserve `clients` au seul compte d'Anissa),
 * mauvais projet Supabase. Traiter ça comme une suppression massive
 * effacerait le cockpit ; le traiter comme « tout est local » le réécrirait
 * en entier. On ne fait ni l'un ni l'autre.
 */
export function lectureEstSuspecte(nbFichesEnBase, nbFichesEnLocal) {
  return Number(nbFichesEnBase) === 0 && Number(nbFichesEnLocal) > 0;
}

// ─── Registre local des identifiants déjà vus en ligne ────────────────────
//
// Il ne contient QUE des identifiants. Aucun nom, aucune donnée clinique.

export function lireVusEnLigne() {
  try {
    const brut = JSON.parse(localStorage.getItem(VUS_EN_LIGNE_KEY) || '[]');
    return new Set(Array.isArray(brut) ? brut.filter((x) => typeof x === 'string') : []);
  } catch {
    return new Set();
  }
}

/**
 * Enregistre les identifiants observés dans une lecture de la base.
 *
 * ⚠ À appeler avec TOUTES les fiches lues, y compris celles en corbeille :
 * une fiche en corbeille a bel et bien été vue en ligne, et c'est justement
 * celle qu'il ne faut pas repousser.
 */
export function marquerVusEnLigne(ids) {
  const liste = Array.isArray(ids) ? ids : [...(ids || [])];
  if (liste.length === 0) return lireVusEnLigne();
  const vus = lireVusEnLigne();
  for (const id of liste) {
    if (typeof id === 'string' && id) vus.add(id);
  }
  const aStocker = [...vus].slice(-MAX_VUS);
  try { localStorage.setItem(VUS_EN_LIGNE_KEY, JSON.stringify(aStocker)); } catch { /* quota */ }
  return new Set(aStocker);
}

export function dejaVuEnLigne(id) {
  return lireVusEnLigne().has(id);
}

/** Remet le registre à zéro. Réservé aux tests et à un diagnostic explicite. */
export function reinitialiserVusEnLigne() {
  try { localStorage.removeItem(VUS_EN_LIGNE_KEY); } catch { /* no-op */ }
}
